/**
 * Utility functions for handling different data sources
 */

// Define shared interfaces for the project components
export interface Feature {
  id: string;
  name: string;
  status: string;
  startDate: string;
  endDate: string;
  peopleAllocated: number;
  blockers?: number;
  tasksCompleted?: number;
  totalTasks?: number;
  source?: string;
  description?: string;
  assignee?: {
    name: string;
    avatar?: string;
  };
  priority?: string;
  storyPoints?: number;
  completion?: number;
  progress?: number;
  tasks?: any[];
}

export interface Epic {
  id: string;
  name: string;
  description?: string;
  status: string;
  startDate: string;
  endDate: string;
  progress?: number;
  peopleAllocated?: number;
  blockers?: number;
  team?: string;
  completion?: number;
  features: Feature[];
  source?: string;
}

export interface Project {
  id: string;
  name: string;
  source?: string;
  status: string;
  startDate: string;
  endDate: string;
  peopleAllocated: number;
  completion?: number;
  epics: Epic[];
  blockers?: number;
  completionPercentage?: number;
  tasks?: number;
  tasksCompleted?: number;
  description?: string;
}

// Helper function for completion percentage
export const getCompletionPercentage = (completed: number, total: number): number => {
  if (total === 0) return 0;
  return Math.round((completed / total) * 100);
};

// Convert API status to internal format
export const convertStatusFormat = (status: string): string => {
  const statusLower = status.toLowerCase();
  
  // Map API status values to internal format
  switch (statusLower) {
    case "delayed":
      return "delayed";
    case "completed":
      return "completed";
    case "blocked":
      return "blocked";
    case "not_started":
    case "not started":
      return "not_started";
    case "cache_in_progress":
    case "cache in progress":
      return "cache_in_progress";
    case "on_track":
    case "on track":
      return "on_track";
    default:
      return "on_track"; // Default to on_track for unknown statuses
  }
};

/**
 * Get source from object that might have a source property or from ID prefix
 */
export const getSourceFromObject = (obj: any): string => {
  // Try to get source directly from object
  if (obj && obj.source) {
    return obj.source.toLowerCase();
  }
  
  // Try to get source from ID prefix
  if (obj && obj.id) {
    if (obj.id.startsWith('jira-')) return 'jira';
    if (obj.id.startsWith('asana-')) return 'asana';
    if (obj.id.startsWith('cadence-')) return 'cadence';
    if (obj.id.startsWith('monday-')) return 'monday';
    if (obj.id.startsWith('azure-') || obj.id.includes('azure')) return 'azure';
  }
  
  return '';
};

/**
 * Get the project/group title based on source
 */
export const getProjectTitle = (source: string): string => {
  switch(source.toLowerCase()) {
    case 'azure':
      return 'Azure DevOps Project';
    case 'jira':
      return 'Jira Project';
    case 'asana':
      return 'Asana Project';
    case 'monday':
      return 'Monday Board';
    case 'cadence':
      return 'CadencePro Project';
    default:
      return 'Project Details';
  }
};

/**
 * Get the epic/roadmap title based on source
 */
export const getEpicTitle = (source: string): string => {
  switch(source.toLowerCase()) {
    case 'azure':
      return 'Azure DevOps Roadmap';
    case 'jira':
      return 'Jira Epic';
    case 'asana':
      return 'Asana Portfolio';
    case 'monday':
      return 'Monday Group';
    case 'cadence':
      return 'CadencePro Epic';
    default:
      return 'Epic Details';
  }
};

/**
 * Get the feature/task title based on source
 */
export const getFeatureTitle = (source: string): string => {
  switch(source.toLowerCase()) {
    case 'azure':
      return 'Azure DevOps Project';
    case 'jira':
      return 'Jira Issue';
    case 'asana':
      return 'Asana Task';
    case 'monday':
      return 'Monday Item';
    case 'cadence':
      return 'CadencePro Feature';
    default:
      return 'Feature Details';
  }
};

/**
 * Get the project entity name based on source
 */
export const getProjectEntityName = (source: string): string => {
  switch(source.toLowerCase()) {
    case 'azure':
      return 'Project';
    case 'jira':
      return 'Project';
    case 'asana':
      return 'Project';
    case 'monday':
      return 'Board';
    case 'cadence':
      return 'Project';
    default:
      return 'Project';
  }
};

/**
 * Get the epic entity name based on source
 */
export const getEpicEntityName = (source: string): string => {
  switch(source.toLowerCase()) {
    case 'azure':
      return 'Roadmap';
    case 'jira':
      return 'Epic';
    case 'asana':
      return 'Portfolio';
    case 'monday':
      return 'Group';
    case 'cadence':
      return 'Epic';
    default:
      return 'Epic';
  }
};

/**
 * Get the feature entity name based on source
 */
export const getFeatureEntityName = (source: string): string => {
  switch(source.toLowerCase()) {
    case 'azure':
      return 'Project';
    case 'jira':
      return 'Issue';
    case 'asana':
      return 'Task';
    case 'monday':
      return 'Item';
    case 'cadence':
      return 'Feature';
    default:
      return 'Feature';
  }
};

/**
 * Get the task entity name based on source
 */
export const getTaskEntityName = (source: string): string => {
  switch(source.toLowerCase()) {
    case 'azure':
      return 'Work Item';
    case 'jira':
      return 'Subtask';
    case 'asana':
      return 'Subtask';
    case 'monday':
      return 'Subitem';
    case 'cadence':
      return 'Task';
    default:
      return 'Task';
  }
};

// /**
//  * Get button text for roadmap view
//  */
// export const getRoadmapButtonText = (source: string): string => {
//   switch(source.toLowerCase()) {
//     case 'azure':
//       return 'View Strategic Map';
//     case 'jira':
//       return 'View Epic';
//     case 'asana':
//       return 'View Project';
//     case 'monday':
//       return 'View Group';
//     case 'cadence':
//       return 'View Roadmap';
//     default:
//       return 'View Roadmap';
//   }
// };

/**
 * Get button text for tasks view
 */
export const getTaskButtonText = (source: string): string => {
  switch(source.toLowerCase()) {
    case 'azure':
      return 'View Tasks';
    case 'jira':
      return 'View Subtasks';
    case 'asana':
      return 'View Subtasks';
    case 'monday':
      return 'View Subitems';
    case 'cadence':
      return 'View Tasks';
    default:
      return 'View Tasks';
  }
}; 